import { ChangeDetectionStrategy, Component, Input, inject } from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { DatePickerComponent } from '@shared/components/date-picker/date-picker.component';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MemberAssociationSelectorComponent } from '@shared/components/member-association-selector/member-association-selector.component';
import { ChurchEventSelection, CivilEventPreview, ImportEvent } from '@core/api/services/portal-member-import.service';

@Component({ selector: 'app-church-import-comparison', standalone: true, imports: [CommonModule, FormsModule, MatIconModule, MatTooltipModule, DatePickerComponent, MemberAssociationSelectorComponent], templateUrl: './church-import-comparison.component.html', styleUrl: './church-import-comparison.component.scss', changeDetection: ChangeDetectionStrategy.OnPush })
export class ChurchImportComparisonComponent {
  onSummaryClick(event: Event, excluded: boolean): void {
    if (excluded) event.preventDefault();
  }
  @Input({ required: true }) events: ImportEvent[] = [];
  @Input() civilEvents: CivilEventPreview[] = [];
  @Input() selections: ChurchEventSelection[] = [];
  @Input() warnings: string[] = [];
  @Input() birthDate?: string | null;
  private readonly dateAdapter = inject<DateAdapter<any>>(DateAdapter);
  private readonly dateValues = new Map<string, { source: string | null; value: any }>();
  readonly today = this.dateAdapter.today();
  get earliestDate(): any { return this.dateValue('birthDate', this.birthDate); }
  dateWarning(event: ImportEvent | CivilEventPreview, index: number, section: ChurchEventSelection['section']): string | undefined {
    if (this.choice(index, section).date) return section === 'civil' ? this.civilIssue(event as CivilEventPreview, index) : undefined;
    return (section === 'civil' ? this.civilIssue(event as CivilEventPreview, index) : undefined)
      ?? this.incompleteDateWarning(event.rawDate ?? event.date, section === 'church' ? 'Acontecimientos' : 'Historia civil')
      ?? (!event.date ? 'Falta una fecha completa y válida.' : undefined);
  }
  selectedDate(index: number, section: ChurchEventSelection['section']): any {
    return this.dateValue(`${section}:${index}`, this.choice(index, section).date);
  }
  private dateValue(key: string, source: string | null | undefined): any {
    const normalized = source ?? null;
    const cached = this.dateValues.get(key);
    if (cached && cached.source === normalized) return cached.value;
    const value = this.dateAdapter.deserialize(normalized);
    this.dateValues.set(key, { source: normalized, value });
    return value;
  }
  setDate(index: number, section: ChurchEventSelection['section'], value: any): void {
    const valid = value && this.dateAdapter.isValid(value)
      && this.dateAdapter.compareDate(value, this.today) <= 0
      && (!this.earliestDate || this.dateAdapter.compareDate(value, this.earliestDate) >= 0);
    this.choice(index, section).date = valid ? this.dateAdapter.toIso8601(value).split('T')[0] : null;
  }
  civilIssue(event: CivilEventPreview, index: number): string | undefined {
    return this.choice(index, 'civil').date && /fecha/i.test(event.issue ?? '') ? undefined : event.issue ?? undefined;
  }
  get allImportableIncluded(): boolean {
    const selections = this.importableSelections();
    return selections.length > 0 && selections.every(selection => selection.include);
  }
  get someImportableIncluded(): boolean { return this.importableSelections().some(selection => selection.include); }
  toggleAllImport(include: boolean): void { this.importableSelections().forEach(selection => selection.include = include); }
  incompleteDateWarning(date: string | null | undefined, section: 'Acontecimientos' | 'Historia civil'): string | undefined {
    return this.warnings.find(warning => /fecha incompleta/i.test(warning)
      && warning.toLocaleLowerCase().includes(section.toLocaleLowerCase())
      && (!date || warning.includes(`(${date})`)));
  }
  private importableSelections(): ChurchEventSelection[] {
    const selections: ChurchEventSelection[] = [];
    this.events.forEach((event, index) => { if (event.isImportable !== false) selections.push(this.choice(index, 'church')); });
    this.civilEvents.forEach((event, index) => { if (event.targetEventType) selections.push(this.choice(index, 'civil')); });
    return selections;
  }
  choice(index: number, section: ChurchEventSelection['section']): ChurchEventSelection {
    let selection = this.selections.find(item => item.index === index && item.section === section);
    if (!selection) { selection = { index, section, include: true }; this.selections.push(selection); }
    return selection;
  }
}
